<script setup lang="ts">
/**
 * SkillsPanel — Collapsible panel for skill level quick-set buttons
 * Shows "All V" / "All IV" / "All III" presets.
 * Future: per-skill overrides, skill requirement list.
 */
import { ref, computed } from 'vue';
import { useFitStore } from '../../stores/fitStore';

const store = useFitStore();
const collapsed = ref(true);

const emit = defineEmits<{
  'set-all-skills': [level: number];
}>();

const currentLevel = computed(() => store.skillLevel ?? 5);

function setSkills(level: number): void {
  emit('set-all-skills', level);
}
</script>

<template>
  <div class="skills-panel">
    <button class="skills-header" @click="collapsed = !collapsed">
      <svg
        width="12" height="12" viewBox="0 0 12 12"
        class="chevron"
        :class="{ expanded: !collapsed }"
      >
        <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      </svg>
      <span>Skills</span>
    </button>

    <div v-if="!collapsed" class="skills-body">
      <div class="skill-presets">
        <span class="preset-label">Quick set:</span>
        <button class="preset-btn" :class="{ active: currentLevel === 5 }" @click="setSkills(5)" title="Set all skills to V">All V</button>
        <button class="preset-btn" :class="{ active: currentLevel === 4 }" @click="setSkills(4)" title="Set all skills to IV">All IV</button>
        <button class="preset-btn" :class="{ active: currentLevel === 3 }" @click="setSkills(3)" title="Set all skills to III">All III</button>
      </div>
      <p class="skills-note">
        Skills affect damage, tank, navigation, and fitting. Default: All V.
      </p>
    </div>
  </div>
</template>

<style scoped>
.skills-panel {
  border-top: 1px solid var(--border-color);
}

.skills-header {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  transition: color var(--transition-fast);
}

.skills-header:hover {
  color: var(--text-primary);
}

.chevron {
  transition: transform var(--transition-fast);
  flex-shrink: 0;
}

.chevron.expanded {
  transform: rotate(90deg);
}

.skills-body {
  padding: 4px 10px 8px;
}

.skill-presets {
  display: flex;
  align-items: center;
  gap: 6px;
}

.preset-label {
  font-size: 0.68rem;
  color: var(--text-secondary);
}

.preset-btn {
  padding: 2px 10px;
  font-size: 0.68rem;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--bg-hover);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}

.preset-btn:hover,
.preset-btn.active {
  background: var(--accent-blue);
  border-color: var(--accent-blue);
  color: white;
}

.skills-note {
  font-size: 0.65rem;
  color: var(--text-secondary);
  margin-top: 6px;
  line-height: 1.4;
}
</style>
